/**
 * Node id used for the controller's implicit root. Guaranteed to be unique
 * because the leading-and-trailing double-underscore form is reserved for
 * the library — real item ids never collide with it.
 */
export const ROOT_NODE_ID = "__root__";

/** Identity of an item or node key. Accepts string or numeric ids. */
export type ID = string | number;

/**
 * Identity of a single fetch request. Discriminated so that illegal
 * combinations (mixing `after` with `before`, stray `from_last: false`,
 * etc.) are unrepresentable.
 *
 *   - `{ kind: "after", cursor: null }`     — bootstrap (first page).
 *   - `{ kind: "after", cursor: "<c>" }`    — forward-scroll load.
 *   - `{ kind: "before", cursor: "<c>" }`   — backward-scroll load.
 *   - `{ kind: "fromLast" }`                — jump to the node's last page.
 */
export type PageRequestKey =
  | { kind: "after"; cursor: ID | null }
  | { kind: "before"; cursor: ID }
  | { kind: "fromLast" };

/**
 * A single page of items fetched from the backend.
 * Stores both raw items and pre-computed IDs for O(1) dedup/boundary checks.
 */
export interface Page<T> {
  readonly items: readonly T[];
  /** Pre-computed keys via getItemId — used for dedup and boundary Sets. */
  readonly itemIds: readonly ID[];
  /** Identity of the fetch request that produced this page — used for dedup and reload. */
  readonly requestCursor: PageRequestKey;
  /** Response cursor pointing at the page BEFORE this one. `null` ⇒ this is the first page. */
  readonly beforeCursor: string | null;
  /** Response cursor pointing at the page AFTER this one. `null` ⇒ this is the last page. */
  readonly afterCursor: string | null;
  /**
   * Absolute index of `items[0]` within the parent node's scope at the
   * moment this page was fetched. `null` for non-leaf depths (group pages
   * carry no row-number semantics); a concrete `number` at the leaf level.
   * Maintained client-side only on `removeItem` — pages strictly AFTER
   * the affected page shift by `-1`. All other in-file page
   * reconstructions preserve the existing value.
   */
  readonly firstItemOffsetInScope: number | null;
}

/**
 * Single-request input to `PageFetcher.fetch`. Carries the discriminated
 * cursor identity plus side-channels for cancellation and parent state.
 */
export interface PageFetchRequest {
  key: PageRequestKey;
  pageSize: number;
  /** Pass to fetch()/XMLHttpRequest for real HTTP cancellation in production. */
  signal?: AbortSignal;
  /**
   * Mirror of the immediate parent node's `extraData` (i.e. the last entry of
   * the `ParentChain` argument). Kept as a convenience side-channel for
   * fetchers that only need the closest ancestor's data without walking the
   * chain — for the full ancestor chain see the first argument of
   * `PageFetcher.fetch`.
   */
  extraData?: Record<string, unknown>;
}

/** Response shape every fetcher implementation must return. */
export interface PageFetchResult<T> {
  items: T[];
  /** Response cursor pointing at the page BEFORE the returned one (`null` ⇒ first page). */
  beforeCursor: string | null;
  /** Response cursor pointing at the page AFTER the returned one (`null` ⇒ last page). */
  afterCursor: string | null;
  /**
   * Absolute index of `items[0]` within the parent scope. Optional at the
   * library boundary — only leaf fetchers populate it; non-leaf fetchers
   * leave it undefined, which the controller stores
   * as `null` to mark "no row-number semantics at this depth".
   */
  startIndex?: number;
}

/**
 * One ancestor node projected for consumption by a fetcher. Carries the
 * ancestor's id and `extraData` (populated when the ancestor was created via
 * the parent fetcher's `getItemExtraData`). Consumers walk a `ParentChain` to
 * gather context from every level above the node being fetched.
 */
export interface ParentItem {
  readonly id: ID;
  readonly extraData: Record<string, unknown>;
}

/**
 * Ancestor chain passed to `PageFetcher.fetch`, ordered by depth:
 *
 *   parents[0]                    — depth 0 ancestor (outermost group)
 *   parents[parents.length - 1]   — immediate parent (depth N-1)
 *
 * Empty array when the fetch targets the root node's direct children
 * (depth 0). The chain never contains the implicit root — the root has no
 * meaningful `extraData` and would only add noise.
 */
export type ParentChain = readonly ParentItem[];

/**
 * Fetcher for a single depth level. Framework-agnostic.
 *
 * `parents` carries the depth-ordered ancestor chain (see {@link ParentChain}).
 * Fetchers that only need the immediate parent's id can read
 * `parents[parents.length - 1]?.id`; fetchers that need every ancestor's
 * `extraData` (for example to collect per-level group context) iterate the
 * array forward.
 */
export interface PageFetcher<T = unknown> {
  fetch: (parents: ParentChain, request: PageFetchRequest) => Promise<PageFetchResult<T>>;
  getItemId: (item: T) => ID;
  /**
   * Extract extra data from a fetched item to store on the child node.
   * Called by the controller for each item when creating child nodes.
   * Leaf-level fetchers can omit this — their items have no children.
   */
  getItemExtraData?: (item: T) => Record<string, unknown>;
  pageSize: number;
}

/**
 * One fetcher per depth level. Array length = total depth levels.
 * Last entry = leaf level (deepest). No artificial limit on depth.
 */
export type PaginationConfig = readonly PageFetcher[];

/** Controller settings. Framework-agnostic. */
export interface PaginationOptions {
  /**
   * Fetchers per depth level. config.length = total depth levels.
   * maxDepth = config.length - 1 (auto-derived by controller).
   */
  config: PaginationConfig;

  /**
   * Symmetric per-level page budget. Applies to BOTH sides of the
   * anchor-span at the same depth:
   *   SUM(pages strictly above top-anchor at depth N) <= maxPages
   *   SUM(pages strictly below bottom-anchor at depth N) <= maxPages
   *
   * With `maxPages = 1` the resident window at each depth converges
   * to ~3 pages (1 above + anchor span + 1 below) during steady scroll.
   */
  maxPages: number;

  /**
   * Load-trigger danger zone: number of records from each end of a
   * node's loaded sequence that fire `tryAfterLoad` / `tryBeforeLoad`
   * when a visible id falls into them. Controls UX prefetch latency —
   * "start the next page when the user is within N records of the
   * edge".
   *
   * NOTE: this knob is for the LOAD trigger only. The TRIM gate uses
   * the per-depth budget `config[depth].pageSize × maxPages` directly
   * — a single source of truth for the trim window so it scales with
   * the page sizes you've configured per level.
   *
   * Absolute count (not a percentage) for two reasons:
   *   1. Predictability — "load when within N records of the edge"
   *      stays meaningful regardless of total dataset size.
   *   2. Guaranteed middle gap — when total > 2 * edgeThreshold there
   *      is always a "calm middle" where neither nearStart nor nearEnd
   *      fires, so walkNode can return null cleanly.
   */
  edgeThreshold: number;

  /**
   * Called after processQueue drains the FIFO queue.
   * When set, `update()` auto-triggers `processQueue()`.
   * When unset, the queue must be drained manually via `flush()`.
   */
  onChange?: (result: FlushResult) => void;

  /**
   * Called when the controller starts or finishes a fetch during processQueue.
   * `request` is the LoadRequest being executed, or `null` when loading ends.
   */
  onLoadingChange?: (request: LoadRequest | null) => void;

  /**
   * Called when process() completes (in the `finally` block), regardless of outcome.
   * Use this to detect when the controller finished but found nothing to do
   * (stale visible IDs) — the adapter can schedule a retry with fresh data.
   */
  onProcessComplete?: (result: FlushResult) => void;

  /**
   * Fires when one or more leaf pages enter the tree under a single
   * node. `pages` are the actual instances held in `node.pages`.
   * Subscriber errors propagate to the caller of `applyFetchResult`.
   */
  onLeafPageLoaded?: (pages: ReadonlyArray<Page<unknown>>, nodeId: ID) => void;

  /**
   * Fires when one or more leaf pages leave the tree under a single
   * node — trim, reload-replace, `clear()`, collapse, leaf detach.
   * Paired with `onLeafPageLoaded` on reload (drop first, then load).
   * `replaceItem`/`removeItem` are item-level and do NOT fire this
   * page-level callback. Subscriber errors propagate to the caller.
   */
  onLeafPageDropped?: (pages: ReadonlyArray<Page<unknown>>, nodeId: ID) => void;

  /**
   * Branch-depth counterpart of `onLeafPageLoaded`. Fires when one or
   * more pages enter the tree under a node whose depth is BELOW the
   * leaf level — i.e. a node whose children are themselves loaded
   * via a fetcher. Useful for adapters that need to mirror the same
   * ownership / prefetch lifecycle they run for leaf records on the
   * group items those pages carry.
   */
  onBranchPageLoaded?: (pages: ReadonlyArray<Page<unknown>>, nodeId: ID) => void;

  /** Branch-depth counterpart of `onLeafPageDropped`. */
  onBranchPageDropped?: (pages: ReadonlyArray<Page<unknown>>, nodeId: ID) => void;
}

/** Position of trimmed content relative to the viewport. */
export type TrimPosition = "above-viewport" | "below-viewport";

/** A single-page trim operation with spatial info for scroll correction. */
export interface PageTrimOp {
  nodeId: ID;
  from: "top" | "bottom";
  position: TrimPosition;
}

/**
 * Describes a fetch that the adapter should execute.
 * Controller does NOT fetch — it returns what needs to be fetched.
 * After fetch completes, adapter calls controller.applyFetchResult().
 */
export interface LoadRequest {
  nodeId: ID;
  depth: number;
  /**
   * Direction meaningful for loader-position UI (after ⇒ bottom loader,
   * before ⇒ top loader). `fromLast` is a `"before"` request — it
   * arrives from above as the user scrolls UP past a node's first page.
   */
  direction: "after" | "before";
  key: PageRequestKey;
  pageSize: number;
  /** When true, replace existing pages in-place instead of appending. */
  reload?: boolean;
}

/** Outcome of a single load executed during processQueue. */
export interface LoadResult {
  request: LoadRequest;
  status: "applied" | "skipped" | "aborted";
}

/**
 * Aggregated result of a full processQueue drain cycle.
 * Passed to `onChange` callback after all ops are applied.
 */
export interface FlushResult {
  stateChanged: boolean;
  trims: PageTrimOp[];
  loads: LoadResult[];
}

/**
 * A queued visibility snapshot waiting to be processed.
 *
 * `ReadonlySet` is intentional — once enqueued, the snapshot is treated
 * as immutable. The controller only reads `.has()` / iterates, never
 * mutates the underlying sets.
 */
export interface PendingSnapshot {
  readonly visibleIds: ReadonlySet<ID>;
  readonly collapsedIds: ReadonlySet<ID>;
}

/**
 * External state required to project branch items. Owned by the
 * controller and threaded through every recursive call in
 * `branchProjection.ts` and `loadPlanner.ts` so those modules stay
 * pure functions of `(node, ctx)`.
 *
 *   - `collapsedIds` — same set used by `getBranchItems`'s caller (the
 *     cleaned collapsed view). Never mutated.
 *   - `maxDepth` — `config.length - 1`. Branch projection stops at
 *     depth `>= maxDepth` (those are leaves).
 */
export interface BranchProjectionCtx {
  readonly collapsedIds: ReadonlySet<ID>;
  readonly maxDepth: number;
}

/**
 * Read-only snapshot of pagination state for debug / monitoring consumers.
 * Returned by `PaginationController.getStats()` via a single O(N) walk over
 * the live node index; safe to call any time without affecting controller
 * behavior.
 */
export interface PaginationStats {
  /** Sum of `node.pages.pages.length` across every node in the tree. */
  readonly totalPages: number;
  /** Pages held by nodes at `depth === maxDepth`. */
  readonly leafPages: number;
  /** Pages held by nodes at `depth < maxDepth` (root + intermediate groupings). */
  readonly branchPages: number;
  /** Size of the live node index (includes the implicit root node). */
  readonly totalNodes: number;
  /** Count of nodes at `depth === maxDepth`. */
  readonly leafNodes: number;
  /** Count of nodes at `depth < maxDepth`. */
  readonly branchNodes: number;
  /** Sum of leaf-level page item counts (records loaded across leaves). */
  readonly leafItemsCount: number;
  /** `config.length - 1`. */
  readonly maxDepth: number;
  /** Monotonic version counter — bumped on every state-changing cycle. */
  readonly epoch: number;
  /** True while a fetch is in flight. */
  readonly isLoading: boolean;
  /** True when no processing / loading is in flight and no pending snapshot. */
  readonly isIdle: boolean;
  /** True when a visibility snapshot is queued. */
  readonly hasPending: boolean;
  /** `PaginationOptions.maxPages` — per-side page budget per depth. */
  readonly maxPages: number;
  /** `PaginationOptions.edgeThreshold` — records-from-edge load trigger. */
  readonly edgeThreshold: number;
  /** Per-level page size, one entry per depth (from `config[depth].pageSize`). */
  readonly pageSizes: readonly number[];
}
